import React, { useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import routes from '../../../setup/consts/routes';
import { THUMB_COLORS } from '../../../theme/brandColors';
import { useOrderDetails } from '../../../components/organisms/Modal/templates/OrderSummaryModal/hooks';
import WebhookLogsModal from '../../../components/organisms/Modal/templates/WebhookLogsModal';
import useHeaderTitleContext from '../../../hooks/useHeaderTitleContext';
import Spinner from '../../../components/atoms/Spinner';
import { SpinnerMode, SpinnerSize } from '../../../components/atoms/Spinner/enums';
import { mq } from '../../../theme/mediaQueries';

// ── layout ──────────────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: var(--s-5);
  align-items: start;

  ${mq.ltLg} {
    grid-template-columns: 1fr;
  }
`;

const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
  min-width: 0;
`;

const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
  min-width: 0;
  position: sticky;
  top: calc(var(--topbar-h, 64px) + 24px);

  ${mq.ltLg} {
    position: static;
  }
`;

const TwoUp = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--s-4);

  ${mq.ltSm} {
    grid-template-columns: 1fr;
  }
`;

// ── card primitives ──────────────────────────────────────────────────────────

const Card = styled.div<{ $pad?: boolean }>`
  background: var(--surface);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-1);
  padding: ${({ $pad = true }) => ($pad ? 'var(--s-5)' : '0')};
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--s-4) var(--s-5);
  border-bottom: 1px solid var(--line);
`;

const CardHeadTitle = styled.h3`
  font-size: var(--t-base);
  font-weight: 600;
  color: var(--ink);
  margin: 0;
`;

const SectionLabel = styled.div`
  font-size: var(--t-xs);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: var(--s-3);
`;

// ── status badge mapping ─────────────────────────────────────────────────────

const STATUS: Record<string, { bg: string; dot: string; label: string; banner?: boolean }> = {
  delivered:      { bg: 'rgba(118,177,89,.09)',   dot: 'var(--brand-green)',  label: 'Delivered' },
  failed:         { bg: 'rgba(212,121,116,.09)',   dot: 'var(--danger)',       label: 'Failed',         banner: true },
  return_pending: { bg: 'rgba(212,121,116,.09)',   dot: 'var(--danger)',       label: 'Return pending', banner: true },
  returned:       { bg: 'rgba(218,146,19,.09)',    dot: 'var(--warning)',      label: 'Returned',       banner: true },
  pending:        { bg: 'rgba(9,15,56,.04)',       dot: 'var(--muted-2)',      label: 'Pending' },
  in_progress:    { bg: 'rgba(74,107,215,.09)',    dot: 'var(--info)',         label: 'In progress' },
};

// ── small icon components ────────────────────────────────────────────────────

const ChevL = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const ChevR = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const WarnIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const DownloadIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);
const BoltIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);
const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/>
  </svg>
);
const BoxIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);
const MapPinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);

// ── button helpers ───────────────────────────────────────────────────────────

const Btn = styled.button<{ $kind?: 'outline' | 'primary' | 'ink' | 'ghost' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: var(--r-md);
  font: inherit;
  font-size: var(--t-sm);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--dur-fast) var(--ease-out),
              border-color var(--dur-fast) var(--ease-out),
              box-shadow var(--dur-fast) var(--ease-out);

  ${({ $kind = 'outline' }) => {
    switch ($kind) {
      case 'primary':
        return `background: var(--brand-green); color: var(--surface); border: 1px solid var(--brand-green);
          &:hover { background: var(--brand-green-2); border-color: var(--brand-green-2); }`;
      case 'ink':
        return `background: var(--brand-ink); color: var(--surface); border: 1px solid var(--brand-ink);
          &:hover { background: var(--brand-ink-2); }`;
      case 'ghost':
        return `background: transparent; color: var(--ink); border: 1px solid transparent;
          &:hover { background: var(--paper-2); }`;
      case 'danger':
        return `background: rgba(212,121,116,.08); color: var(--danger); border: 1px solid rgba(212,121,116,.3);
          &:hover { background: rgba(212,121,116,.14); }`;
      default:
        return `background: var(--surface); color: var(--ink); border: 1px solid var(--line);
          &:hover { background: var(--paper-2); }`;
    }
  }}

  &:disabled { opacity: 0.4; pointer-events: none; }
  svg { flex-shrink: 0; }
`;

const BtnSm = styled(Btn)`
  height: 30px;
  padding: 0 10px;
  font-size: var(--t-xs);
`;

const IconBtn = styled(Btn)`
  width: 36px;
  padding: 0;
  justify-content: center;
`;

// ── KV row ───────────────────────────────────────────────────────────────────

const KVRow: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--line)', gap: 12 }}>
    <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', fontWeight: 500, flexShrink: 0 }}>{k}</span>
    <span style={{ fontSize: 'var(--t-sm)', fontWeight: 600, minWidth: 0, textAlign: 'right' }}>{v}</span>
  </div>
);

// ── TotalsRow ────────────────────────────────────────────────────────────────

const TotalsRow: React.FC<{ label: string; value: string; big?: boolean }> = ({ label, value, big }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '5px 0' }}>
    <span style={{ fontSize: big ? 'var(--t-base)' : 'var(--t-sm)', fontWeight: big ? 700 : 500, color: big ? 'var(--ink)' : 'var(--muted)' }}>
      {label}
    </span>
    <span style={{ fontWeight: big ? 700 : 600, fontSize: big ? 'var(--t-lg)' : 'var(--t-sm)', fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </span>
  </div>
);

// ── Avatar initials ──────────────────────────────────────────────────────────

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ── item thumbnail color from index ─────────────────────────────────────────

// ── main component ───────────────────────────────────────────────────────────

const OrderDetailPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const history = useHistory();
  const intl = useIntl();
  const { setHeaderTitle } = useHeaderTitleContext();

  const goBack = () => history.push(routes.ORDERS);
  const { order, loading } = useOrderDetails(orderId, goBack);
  const [webhookLogsOpen, setWebhookLogsOpen] = useState(false);

  React.useEffect(() => {
    setHeaderTitle(intl.formatMessage({ id: 'orderDetail.title', defaultMessage: 'Order detail' }));
  }, [intl, setHeaderTitle]);

  const statusKey = useMemo(() => order?.deliveryStatus?.toLowerCase().replace(/\s+/g, '_') ?? '', [order?.deliveryStatus]);
  const statusMeta = STATUS[statusKey];

  const subtotal = useMemo(() => order?.deliveryItems.reduce((s, it) => s + it.price * it.quantity, 0) ?? 0, [order]);
  const delivery = order?.deliveryFee ?? 0;
  const grandTotal = order?.amountIncludeDeliveryCharge ? order.totalPrice : subtotal + delivery;

  const fmt = (n: number) =>
    new Intl.NumberFormat(intl.locale, { maximumFractionDigits: 0 }).format(n) + ' IQD';

  const placedEvent = useMemo(() => order?.deliveryTimeline?.[order.deliveryTimeline.length - 1], [order]);
  const placedDate = placedEvent ? new Date(placedEvent.date).toLocaleDateString(intl.locale, { day: 'numeric', month: 'short', year: 'numeric' }) : null;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <Spinner mode={SpinnerMode.Dark} size={SpinnerSize.Big} />
      </div>
    );
  }

  if (!order) return null;

  const receipt = order.externalReference || order.fulfillmentId;
  const showBanner = !!statusMeta?.banner;

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--s-4)' }}>
        <button
          onClick={goBack}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 10px', borderRadius: 'var(--r-md)',
            background: 'transparent', border: '1px solid var(--line)',
            color: 'var(--muted)', cursor: 'pointer', fontFamily: 'inherit',
            fontSize: 'var(--t-sm)', fontWeight: 500,
          }}
        >
          <ChevL />
          {intl.formatMessage({ id: 'mainNavigation.orders', defaultMessage: 'Orders' })}
        </button>
        <span style={{ color: 'var(--muted-2)', display: 'flex' }}><ChevR /></span>
        <span style={{ fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--ink)' }}>
          {intl.formatMessage({ id: 'orderDetail.orderNumber', defaultMessage: 'Order #{receipt}' }, { receipt })}
        </span>
      </div>

      {/* Page header */}
      <div style={{ marginBottom: 'var(--s-5)' }}>
        {/* Status badge row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
          {statusMeta && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '3px 10px', borderRadius: 'var(--r-full)',
              background: statusMeta.bg, fontSize: 'var(--t-xs)', fontWeight: 600,
              color: statusMeta.dot,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
              {statusMeta.label}
            </span>
          )}
          {placedDate && (
            <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
              {intl.formatMessage({ id: 'orderDetail.placed', defaultMessage: 'Placed' })} {placedDate}
            </span>
          )}
        </div>

        {/* Title */}
        <h1 style={{ fontSize: 'var(--t-3xl)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink)', margin: '0 0 8px' }}>
          {intl.formatMessage({ id: 'orderDetail.orderNumber', defaultMessage: 'Order #{receipt}' }, { receipt })}
        </h1>

        {/* Metadata row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-3)', flexWrap: 'wrap', color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
          {order.fulfillmentId && !/^ful_/.test(order.fulfillmentId) && (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{order.fulfillmentId}</span>
          )}
          {order.entity && !/^ent_/.test(order.entity) && (
            <>
              <span style={{ color: 'var(--muted-2)' }}>·</span>
              <span>{order.entity}</span>
            </>
          )}
        </div>

        {/* Action toolbar */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 'var(--s-4)', flexWrap: 'wrap' }}>
          {!order.isCancelled && (
            <Btn $kind="danger">
              <XIcon />
              {intl.formatMessage({ id: 'orderDetail.cancelOrder', defaultMessage: 'Cancel order' })}
            </Btn>
          )}
          <Btn $kind="outline" onClick={() => history.push(routes.EDIT_ORDER.replace(':orderId', orderId))}>
            <PencilIcon />
            {intl.formatMessage({ id: 'orderDetail.edit', defaultMessage: 'Edit' })}
          </Btn>
        </div>
      </div>

      {/* Status banner */}
      {showBanner && (
        <div style={{
          background: 'var(--danger-soft)',
          border: '1px solid rgba(215,74,71,.18)',
          borderRadius: 'var(--r-lg)',
          padding: 'var(--s-5)',
          marginBottom: 'var(--s-5)',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr auto', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{
              width: 48, height: 48, borderRadius: 'var(--r-md)',
              background: 'rgba(212,121,116,.18)', color: 'var(--danger)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}>
              <WarnIcon />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 'var(--t-lg)', color: 'var(--danger-ink)' }}>
                {statusKey === 'return_pending'
                  ? intl.formatMessage({ id: 'orderDetail.banner.returnPending', defaultMessage: 'This order is on its way back to the warehouse' })
                  : statusKey === 'returned'
                  ? intl.formatMessage({ id: 'orderDetail.banner.returned', defaultMessage: 'This order has been returned to the warehouse' })
                  : intl.formatMessage({ id: 'orderDetail.banner.failed', defaultMessage: 'This order failed to deliver' })}
              </div>
              <div style={{ fontSize: 'var(--t-sm)', color: 'var(--danger-ink)', opacity: 0.8, marginTop: 4, maxWidth: 640 }}>
                {order.failedAttempts?.length
                  ? intl.formatMessage({ id: 'orderDetail.banner.driverFailed', defaultMessage: 'Driver failed {attempts} attempt(s). Re-schedule a new delivery, cancel, or contact the customer directly.' }, { attempts: order.failedAttempts.length })
                  : intl.formatMessage({ id: 'orderDetail.banner.reschedule', defaultMessage: 'Re-schedule a new delivery, cancel, or contact the customer directly.' })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main two-column grid */}
      <Grid>
        <LeftCol>
          {/* Items card */}
          <Card $pad={false}>
            <CardHead>
              <CardHeadTitle>
                {intl.formatMessage({ id: 'orderDetail.items', defaultMessage: 'Items' })}
                <span style={{ fontWeight: 500, color: 'var(--muted)', fontSize: 'var(--t-sm)', marginLeft: 6 }}>
                  · {order.deliveryItems.length} {order.deliveryItems.length === 1
                    ? intl.formatMessage({ id: 'orderDetail.productSingular', defaultMessage: 'product' })
                    : intl.formatMessage({ id: 'orderDetail.productPlural', defaultMessage: 'products' })}
                </span>
              </CardHeadTitle>
              <BtnSm $kind="ghost">{intl.formatMessage({ id: 'orderDetail.addItem', defaultMessage: '+ Add item' })}</BtnSm>
            </CardHead>

            <div style={{ padding: '4px 8px 8px' }}>
              {order.deliveryItems.map((item, i) => (
                <div
                  key={item.id || i}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 14,
                    padding: '14px 12px', flexWrap: 'wrap',
                    borderBottom: i < order.deliveryItems.length - 1 ? '1px solid var(--line)' : 'none',
                  }}
                >
                  {/* Thumbnail */}
                  <div style={{
                    width: 48, height: 48, borderRadius: 'var(--r-sm)',
                    background: item.imageUrl ? 'transparent' : THUMB_COLORS[i % THUMB_COLORS.length],
                    flexShrink: 0, overflow: 'hidden',
                    display: 'grid', placeItems: 'center',
                    color: 'rgba(0,0,0,.25)',
                  }}>
                    {item.imageUrl
                      ? <img src={item.imageUrl} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <BoxIcon />}
                  </div>

                  {/* Name + description */}
                  <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 'var(--t-base)', lineHeight: 1.4, overflowWrap: 'break-word' }}>
                      {item.name}
                    </div>
                    {item.description && (
                      <span style={{
                        display: 'inline-block', marginTop: 5,
                        fontSize: 'var(--t-xs)', color: 'var(--warning-ink)',
                        background: 'var(--warning-soft)', padding: '2px 8px',
                        borderRadius: 'var(--r-full)', fontWeight: 500,
                      }}>
                        {item.description}
                      </span>
                    )}
                  </div>

                  {/* Price block */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0, marginLeft: 'auto' }}>
                    <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{item.quantity}</span>
                      <span style={{ color: 'var(--muted-2)' }}> × </span>
                      {fmt(item.price)}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 'var(--t-base)', textAlign: 'right', whiteSpace: 'nowrap', minWidth: 80, fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(item.price * item.quantity)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals footer */}
            <div style={{ borderTop: '1px solid var(--line)', padding: '14px 20px', background: 'var(--paper)', borderRadius: '0 0 var(--r-lg) var(--r-lg)' }}>
              <TotalsRow label={intl.formatMessage({ id: 'orderDetail.subtotal', defaultMessage: 'Subtotal' })} value={fmt(subtotal)} />
              <TotalsRow label={intl.formatMessage({ id: 'orderDetail.deliveryCharge', defaultMessage: 'Delivery charge' })} value={fmt(delivery)} />
              <TotalsRow label={intl.formatMessage({ id: 'orderDetail.vat', defaultMessage: 'VAT' })} value={fmt(0)} />
              <div style={{ height: 1, background: 'var(--line)', margin: '8px 0' }} />
              <TotalsRow label={intl.formatMessage({ id: 'orderDetail.grandTotal', defaultMessage: 'Grand total' })} value={fmt(grandTotal)} big />
            </div>
          </Card>

          {/* Customer + Address side-by-side */}
          <TwoUp>
            {/* Customer card */}
            <Card $pad={false} style={{ padding: 'var(--s-5)' }}>
              <SectionLabel>{intl.formatMessage({ id: 'orderDetail.customer', defaultMessage: 'Customer' })}</SectionLabel>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%',
                  background: 'var(--brand-green-soft)', color: 'var(--brand-green-ink)',
                  display: 'grid', placeItems: 'center',
                  fontWeight: 700, fontSize: 16, flexShrink: 0,
                }}>
                  {initials(order.customerName || '?')}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 'var(--t-base)' }}>{order.customerName}</div>
                  {order.state && <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', marginTop: 1 }}>{order.state}</div>}
                </div>
              </div>

              {order.customerPhone && <KVRow k={intl.formatMessage({ id: 'forms.fields.phoneNumber', defaultMessage: 'Phone' })} v={<span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{order.customerPhone}</span>} />}
              {order.secondCustomerPhone && <KVRow k={intl.formatMessage({ id: 'forms.fields.secondPhoneNumber', defaultMessage: 'Second phone' })} v={<span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{order.secondCustomerPhone}</span>} />}
              {order.state && (
                <KVRow k={intl.formatMessage({ id: 'customers.city', defaultMessage: 'City' })} v={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 'var(--r-full)', background: 'var(--paper-2)', fontSize: 'var(--t-xs)', fontWeight: 600, color: 'var(--ink-2)' }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--muted)', display: 'inline-block' }} />
                    {order.state}
                  </span>
                } />
              )}

            </Card>

            {/* Address card */}
            <Card $pad={false} style={{ padding: 'var(--s-5)' }}>
              <SectionLabel>{intl.formatMessage({ id: 'orderDetail.deliveryAddress', defaultMessage: 'Delivery address' })}</SectionLabel>
              {order.state && (
                <div style={{ fontSize: 'var(--t-sm)', lineHeight: 1.6, marginBottom: 14, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--muted-2)', display: 'flex' }}><MapPinIcon /></span>
                  {order.state}
                </div>
              )}
              {/* Mini-map — links to Google Maps when coordinates available */}
              {(() => {
                const hasCoords = order.latitude && order.longitude && (order.latitude !== 0 || order.longitude !== 0);
                const mapsUrl = hasCoords
                  ? `https://www.google.com/maps?q=${order.latitude},${order.longitude}`
                  : order.state
                  ? `https://www.google.com/maps/search/${encodeURIComponent(order.state)}`
                  : undefined;
                const mapContent = (
                  <div style={{
                    aspectRatio: '16 / 8', borderRadius: 'var(--r-md)', position: 'relative',
                    background: 'linear-gradient(135deg, var(--brand-green-soft) 0%, var(--paper-2) 100%)',
                    overflow: 'hidden', border: '1px solid var(--line)',
                    cursor: mapsUrl ? 'pointer' : 'default',
                  }}>
                    <svg viewBox="0 0 320 160" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                      <path d="M0,80 Q80,40 160,90 T320,70" stroke="rgba(9,15,56,.14)" strokeWidth="2" fill="none"/>
                      <path d="M40,0 L40,160 M120,0 L120,160 M200,0 L200,160 M280,0 L280,160" stroke="rgba(9,15,56,.05)" strokeWidth="1"/>
                      <path d="M0,30 L320,30 M0,110 L320,110 M0,150 L320,150" stroke="rgba(9,15,56,.05)" strokeWidth="1"/>
                    </svg>
                    <div style={{ position: 'absolute', left: '48%', top: '52%', transform: 'translate(-50%, -100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <div style={{ background: 'var(--danger)', color: 'var(--surface)', padding: '3px 8px', borderRadius: 'var(--r-full)', fontSize: 10, fontWeight: 600 }}>
                        {hasCoords
                          ? intl.formatMessage({ id: 'orderDetail.dropPoint', defaultMessage: 'Drop point' })
                          : order.state}
                      </div>
                      <span style={{ color: 'var(--danger)', display: 'flex' }}><MapPinIcon /></span>
                    </div>
                    {mapsUrl && (
                      <div style={{ position: 'absolute', bottom: 6, right: 8, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', padding: '2px 7px', fontSize: 10, fontWeight: 600, color: 'var(--muted)', boxShadow: 'var(--sh-1)' }}>
                        {intl.formatMessage({ id: 'orderDetail.openInMaps', defaultMessage: 'Open in Maps ↗' })}
                      </div>
                    )}
                  </div>
                );
                return mapsUrl ? (
                  <a href={mapsUrl} target="_blank" rel="noreferrer" style={{ display: 'block', textDecoration: 'none' }}>
                    {mapContent}
                  </a>
                ) : mapContent;
              })()}
            </Card>
          </TwoUp>

          {/* Internal notes */}
          <Card $pad={false} style={{ padding: 'var(--s-5)' }}>
            <SectionLabel>{intl.formatMessage({ id: 'orderDetail.internalNotes', defaultMessage: 'Internal notes' })}</SectionLabel>
            {order.notes ? (
              <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--ink)', lineHeight: 1.6 }}>
                {order.notes}
              </p>
            ) : (
              <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)', fontStyle: 'italic' }}>
                {intl.formatMessage({ id: 'orderDetail.noNotes', defaultMessage: 'No notes' })}
              </p>
            )}
          </Card>
        </LeftCol>

        {/* Right sticky column */}
        <RightCol>
          {/* Shipping label */}
          <Card $pad={false} style={{ padding: 'var(--s-5)' }}>
            <div style={{ marginBottom: 12 }}>
              <CardHeadTitle style={{ marginBottom: 2 }}>{intl.formatMessage({ id: 'orderDetail.shippingLabel', defaultMessage: 'Shipping label' })}</CardHeadTitle>
              <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{intl.formatMessage({ id: 'orderDetail.tracking', defaultMessage: 'Tracking' })} · {receipt}</div>
            </div>

            {/* Label preview */}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href={order.shippingLabelSrc || undefined}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'flex', gap: 12, alignItems: 'center',
                border: '1px solid var(--line)', borderRadius: 'var(--r-md)',
                padding: 12, background: 'var(--paper)', marginBottom: 12,
                textDecoration: 'none',
                cursor: order.shippingLabelSrc ? 'pointer' : 'default',
                transition: 'background var(--dur-fast) var(--ease-out)',
                ...(order.shippingLabelSrc ? {} : { pointerEvents: 'none' }),
              }}
              onMouseEnter={e => { if (order.shippingLabelSrc) (e.currentTarget as HTMLElement).style.background = 'var(--paper-2)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'var(--paper)'; }}
            >
              <div style={{ width: 64, height: 80, borderRadius: 6, background: 'var(--surface)', border: '1px solid var(--line)', padding: 6, flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ width: 14, height: 14, borderRadius: 3, background: 'var(--brand-green)' }} />
                  <div style={{ fontSize: 7, fontWeight: 700, color: 'var(--brand-ink)' }}>SC</div>
                </div>
                <div style={{ display: 'flex', gap: 1.5, height: 18, alignItems: 'flex-end' }}>
                  {Array.from({ length: 18 }).map((_, bi) => (
                    <div key={bi} style={{ flex: 1, background: 'var(--brand-ink)', height: 6 + (bi * 7 % 14) }} />
                  ))}
                </div>
                <div style={{ fontSize: 5, color: 'var(--muted)', textAlign: 'center', fontFamily: 'var(--font-mono)' }}>{fmt(grandTotal)}</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 'var(--t-sm)', fontWeight: 600 }}>{intl.formatMessage({ id: 'orderDetail.readyToPrint', defaultMessage: 'Ready to print' })}</div>
                <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>{intl.formatMessage({ id: 'orderDetail.labelSpec', defaultMessage: 'A6 thermal · AR/EN bilingual' })}</div>
              </div>
            </a>

            {order.shippingLabelSrc ? (
              <a href={order.shippingLabelSrc} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', display: 'block' }}>
                <Btn $kind="ink" style={{ width: '100%', justifyContent: 'center', height: 38 }}>{intl.formatMessage({ id: 'orderDetail.printLabel', defaultMessage: 'Print label' })}</Btn>
              </a>
            ) : (
              <Btn $kind="ink" style={{ width: '100%', justifyContent: 'center', height: 38 }} disabled>{intl.formatMessage({ id: 'orderDetail.printLabel', defaultMessage: 'Print label' })}</Btn>
            )}
          </Card>

          {/* Activity timeline */}
          {order.deliveryTimeline.length > 0 && (
            <Card $pad={false} style={{ padding: 'var(--s-5)' }}>
              <div style={{ marginBottom: 16 }}>
                <CardHeadTitle>{intl.formatMessage({ id: 'orderDetail.activity', defaultMessage: 'Activity' })}</CardHeadTitle>
                <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>{intl.formatMessage({ id: 'orderDetail.mostRecentFirst', defaultMessage: 'Most recent first' })}</div>
              </div>
              <div style={{ position: 'relative' }}>
                {/* Vertical rail */}
                <div style={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: 2, background: 'var(--line)' }} />
                {order.deliveryTimeline.map((ev, i) => (
                  <div key={i} style={{ position: 'relative', paddingLeft: 24, paddingBottom: 14 }}>
                    <span style={{ position: 'absolute', left: 0, top: 2, width: 16, height: 16, borderRadius: 8, background: 'var(--surface)', display: 'grid', placeItems: 'center' }}>
                      <span style={{
                        width: 10, height: 10, borderRadius: 5,
                        background: ev.color || 'var(--muted-2)',
                        boxShadow: i === 0 ? `0 0 0 3px ${ev.color || 'var(--muted-2)'}33` : 'none',
                      }} />
                    </span>
                    <div style={{ fontSize: 'var(--t-sm)', fontWeight: 600, color: i === 0 ? (ev.color || 'var(--ink)') : 'var(--ink)' }}>
                      {ev.title}
                    </div>
                    {ev.description && (
                      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 2 }}>{ev.description}</div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--muted-2)', marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>
                      {new Date(ev.date).toLocaleDateString(intl.locale)} · {ev.time}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Webhook logs */}
          <Card $pad={false} style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => setWebhookLogsOpen(true)}>
            <div style={{
              width: 32, height: 32, borderRadius: 'var(--r-sm)',
              background: 'var(--paper-2)', color: 'var(--muted)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}>
              <BoltIcon />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 'var(--t-sm)', fontWeight: 600 }}>{intl.formatMessage({ id: 'webhookLogs', defaultMessage: 'Webhook logs' })}</div>
              <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 1 }}>
                {intl.formatMessage({ id: 'orderDetail.eventsSent', defaultMessage: '{count} events sent' }, { count: order.deliveryTimeline.length })}
              </div>
            </div>
            <button
              style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--muted)', display: 'grid', placeItems: 'center' }}
              onClick={() => setWebhookLogsOpen(true)}
            >
              <ChevR />
            </button>
          </Card>
        </RightCol>
      </Grid>

      {webhookLogsOpen && order && (
        <WebhookLogsModal order={order} onClose={() => setWebhookLogsOpen(false)} />
      )}
    </div>
  );
};

export default OrderDetailPage;
